import type { Listing } from "@lumora/core";
import { useEffect, useState } from "react";
import { client, SITE } from "../client.js";
import { appListingsFromContent, type AppContentLike } from "./appListings.js";

export type AppListingsStatus = "loading" | "ready" | "error";

export interface AppListingsState {
  status: AppListingsStatus;
  listings: readonly Listing[];
}

export type AppContentFetcher = () => Promise<AppContentLike>;

const defaultFetcher: AppContentFetcher = () => client.getSiteContent(SITE) as Promise<AppContentLike>;

/**
 * Apps from the Lumora site's public content. Pass `override` (tests / previews) to skip the network.
 * Errors resolve to an empty list with status "error" so callers can fall back quietly.
 */
export function useAppListings(
  override?: readonly Listing[],
  fetcher: AppContentFetcher = defaultFetcher,
): AppListingsState {
  const [state, setState] = useState<AppListingsState>({ status: "loading", listings: [] });
  useEffect(() => {
    if (override) return;
    let live = true;
    fetcher().then(
      (content) => live && setState({ status: "ready", listings: appListingsFromContent(content) }),
      () => live && setState({ status: "error", listings: [] }),
    );
    return () => {
      live = false;
    };
  }, [override, fetcher]);
  return override ? { status: "ready", listings: override } : state;
}
